import {
  Component,
  LOCALE_ID,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { SoBadgeUI } from '../so-badge/so-badge.component';
import { SoSwitchUI } from '../so-switch/so-switch.component';
import { SoPremiumIconUI } from '../so-premium-icon/so-premium-icon.component';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';
import { SoInputUI } from '../so-input/so-input.component';
import { SoTabsUI, SoTab } from '../so-tabs/so-tabs.component';
import { SoRowActionsMenuUI } from '../so-row-actions-menu/so-row-actions-menu.component';
import { SoColumnPickerUI, SoColumnPickerColumn } from '../so-column-picker/so-column-picker.component';
import { SoTokenPaginationUI } from '../so-token-pagination/so-token-pagination.component';
import { SoTableUI } from '../so-table/so-table.component';
import {
  SoTableButton,
  SoTableHeader,
  SoTableHeaderStyle,
  SoTablePaginationType,
  SoTableStyle,
  SoTableSwitchEvent,
  SoTableTokenPaginationEvent,
  SoTableTokenPaginationState,
} from '../so-table/so-table.interfaces';
import {
  SoListTableCompactRow,
  SoListTablePersistedState,
  SoListTableStat,
  SoListTableSwitch,
  SoListTableTimeline,
  SoListTableView,
} from './so-list-table.interfaces';

const SEARCH_DEBOUNCE_MS = 500;

@Component({
  selector: 'so-list-table-ui',
  standalone: true,
  imports: [
    CommonModule,
    SoBadgeUI,
    SoSwitchUI,
    SoPremiumIconUI,
    SoButtonGradientUI,
    SoInputUI,
    SoTabsUI,
    SoRowActionsMenuUI,
    SoColumnPickerUI,
    SoTokenPaginationUI,
    SoTableUI,
  ],
  templateUrl: './so-list-table.component.html',
  styleUrl: './so-list-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoListTableUI implements OnDestroy {
  private readonly locale = inject(LOCALE_ID);
  @ViewChild('listSearchInput') private searchInputRef?: SoInputUI;

  data = input.required<Record<string, any>[]>();
  headers = input.required<SoTableHeader[]>();
  compactRow = input.required<SoListTableCompactRow>();

  view = model<SoListTableView>('compact');
  hiddenColumns = model<string[]>([]);

  enableColumnPicker = input<boolean>(true);
  lockedColumns = input<string[]>([]);
  persistKey = input<string>('');
  dateFormat = input<string>('dd MMM y');
  compactViewLabel = input<string>('Compacta');
  denseViewLabel = input<string>('Densa');

  showTableHeader = input<boolean>(true);
  tableTitle = input<string>('');
  tableSubtitle = input<string>('');
  tableHeaderIcon = input<string>('');
  tableHeaderIconColor = input<string>('--so-primary');

  showTableSearch = input<boolean>(false);
  tableSearchPlaceholder = input<string>('Buscar...');
  searchText = input<string>('');

  showTableActionButton = input<boolean>(false);
  tableActionButtonText = input<string>('');
  tableActionButtonIcon = input<string>('');
  tableActionButtonDisabled = input<boolean>(false);

  tableStyle = input<SoTableStyle>('minimal');
  headerStyle = input<SoTableHeaderStyle>('classic');
  showInnerBorders = input<boolean>(true);
  striped = input<boolean>(false);
  stickyLeftColumns = input<number>(0);
  stickyRightColumns = input<number>(0);
  enableRowSelection = input<boolean>(false);

  isActionsEnabled = input<boolean>(false);
  actionsDisplay = input<'inline' | 'menu'>('menu');
  buttonsContainerWidth = input<string>('auto');
  editAction = input<boolean>(true);
  deleteAction = input<boolean>(true);
  customButtons = input<SoTableButton[]>([]);

  showSwitches = input<boolean>(false);
  switchHeader = input<string>('');
  switchIdentifier = input<string>('');
  switchLabelLeft = input<string>('No');
  switchLabelRight = input<string>('Sí');
  switchRowVisibilityFN = input<((item: any) => boolean) | undefined>(undefined);
  switchRowResolverFN = input<((item: any) => boolean) | undefined>(undefined);

  enablePagination = input<boolean>(false);
  /** The compact view only renders a footer for `'token'`; with `'classic'` it shows none (the dense view keeps both). */
  paginationType = input<SoTablePaginationType>('classic');
  itemsPerPage = input<number>(15);
  pageSizeOptions = input<number[]>([10, 25, 50, 100]);
  tokenPaginationState = input<SoTableTokenPaginationState>({
    pageCounter: 0,
    pageSize: 10,
    nextToken: '',
    prevToken: '',
  });

  editEvent = output<Record<string, any>>();
  deleteEvent = output<Record<string, any>>();
  /** Only emitted in the dense view (pass-through from so-table-ui). Compact rows are not clickable as a unit — only their affordances are (side link, switch, kebab). */
  rowClickedEvent = output<Record<string, any> | null>();
  switchEvent = output<SoTableSwitchEvent>();
  tableActionButtonClicked = output<void>();
  tableSearchChanged = output<string>();
  nextPageRequested = output<SoTableTokenPaginationEvent>();
  previousPageRequested = output<SoTableTokenPaginationEvent>();
  pageSizeChanged = output<number>();

  protected internalSearchText = signal<string>('');

  private searchDebounceId: ReturnType<typeof setTimeout> | null = null;
  private restoredKey: string | null = null;

  protected viewTabs = computed<SoTab[]>(() => [
    { title: this.compactViewLabel(), icon: 'view-agenda' },
    { title: this.denseViewLabel(), icon: 'view-headline' },
  ]);

  protected viewIndex = computed(() => (this.view() === 'dense' ? 1 : 0));

  protected showsColumnPicker = computed(() => this.enableColumnPicker() && this.view() === 'dense');

  protected showsCompactPagination = computed(() => this.enablePagination() && this.paginationType() === 'token');

  protected pickerColumns = computed<SoColumnPickerColumn[]>(() =>
    this.headers().map(header => ({
      key: String(header.dataName),
      label: header.header,
      locked: this.lockedColumns().includes(String(header.dataName)),
    }))
  );

  // A locked column can still be present in hiddenColumns (e.g. it was hidden before being
  // locked, or restored from persisted preferences) — the picker forces it checked/disabled,
  // but the table itself must never hide a locked column, or the user is stuck seeing a
  // locked-but-hidden entry they can't uncheck.
  protected effectiveHiddenColumns = computed(() =>
    this.hiddenColumns().filter(key => !this.lockedColumns().includes(key))
  );

  protected rowActions = computed<SoTableButton[]>(() => {
    // so-table-ui (dense) independently gates its own actions column on isActionsEnabled, but
    // the compact row's kebab has no such gate of its own — it just renders whatever this
    // returns. Gate here so a consumer that never opted into actions doesn't get a menu with
    // destructive Editar/Eliminar defaults in the compact view alone.
    if (!this.isActionsEnabled()) return [];

    const actions: SoTableButton[] = [];

    if (this.editAction()) {
      actions.push({
        svg: 'edit',
        color: '--so-secondary',
        tooltip: 'Editar',
        action: (item: any) => this.editEvent.emit(item),
      });
    }

    actions.push(...this.customButtons());

    if (this.deleteAction()) {
      actions.push({
        svg: 'delete',
        color: '--so-danger',
        tooltip: 'Eliminar',
        variant: 'danger',
        action: (item: any) => this.deleteEvent.emit(item),
      });
    }

    return actions;
  });

  constructor() {
    effect(() => {
      const search = this.searchText();
      untracked(() => {
        if (search === this.internalSearchText()) return;
        this.internalSearchText.set(search);
        this.searchInputRef?.writeValue(search);
      });
    }, {  });

    effect(() => {
      const key = this.persistKey();
      const state: SoListTablePersistedState = { view: this.view(), hiddenColumns: this.hiddenColumns() };
      if (!key) return;

      untracked(() => {
        if (this.restoredKey !== key) {
          this.restoredKey = key;
          this.restoreState(key);
          return;
        }
        this.persistState(key, state);
      });
    }, {  });
  }

  ngOnDestroy(): void {
    this.clearSearchDebounce();
  }

  protected onViewTabChanged(index: number): void {
    this.view.set(index === 1 ? 'dense' : 'compact');
  }

  protected onSearchChange(value: string): void {
    this.internalSearchText.set(value);
    this.clearSearchDebounce();
    const debounceId = setTimeout(() => {
      if (this.searchDebounceId !== debounceId) return;
      this.searchDebounceId = null;
      this.tableSearchChanged.emit(value);
    }, SEARCH_DEBOUNCE_MS);
    this.searchDebounceId = debounceId;
  }

  public resetSearch(): void {
    this.clearSearchDebounce();
    this.internalSearchText.set('');
    this.searchInputRef?.writeValue('');
  }

  private clearSearchDebounce(): void {
    if (this.searchDebounceId === null) return;
    clearTimeout(this.searchDebounceId);
    this.searchDebounceId = null;
  }

  protected metaText(item: Record<string, any>): string {
    return this.compactRow().meta(item).filter(entry => !!entry?.trim()).join('  ·  ');
  }

  protected timelineOf(item: Record<string, any>): SoListTableTimeline | null {
    const timeline = this.compactRow().timeline;
    return timeline ? timeline(item) : null;
  }

  protected rangeText(timeline: SoListTableTimeline): string {
    const start = this.formatDateValue(timeline.start);
    const end = timeline.end ? this.formatDateValue(timeline.end) : '';
    return end ? `${start} → ${end}` : start;
  }

  protected progressOf(timeline: SoListTableTimeline): number | null {
    const percent = timeline.progressPercent;
    if (percent === undefined || percent === null) return null;
    return Math.min(100, Math.max(0, percent));
  }

  protected isStatLink(stat: SoListTableStat, item: Record<string, any>): boolean {
    return !!stat.action && this.showsStat(stat, item);
  }

  protected showsStat(stat: SoListTableStat, item: Record<string, any>): boolean {
    return stat.show ? stat.show(item) : true;
  }

  protected onStatLinkClick(stat: SoListTableStat, item: Record<string, any>): void {
    stat.action?.(item);
  }

  protected showsSwitch(item: Record<string, any>): boolean {
    const config = this.compactRow().switchConfig;
    return !!config && (config.show ? config.show(item) : true);
  }

  protected isSwitchDisabled(config: SoListTableSwitch, item: Record<string, any>): boolean {
    return config.disabled ? config.disabled(item) : false;
  }

  private formatDateValue(value: Date | string): string {
    const date = value instanceof Date ? value : this.parseDateOrDateTime(value);
    if (isNaN(date.getTime())) return String(value);
    return formatDate(date, this.dateFormat(), this.locale);
  }

  // A bare date-only ISO string ('2026-08-20') is parsed by `new Date()` as UTC midnight,
  // which formats as the previous local calendar day in negative UTC offsets (e.g. Colombia,
  // México) — append a local time-of-day so it parses as a calendar date, not a UTC instant.
  private parseDateOrDateTime(value: string): Date {
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    return new Date(isDateOnly ? `${value}T00:00:00` : value);
  }

  private storageKey(key: string): string {
    return `so-list-table:${key}`;
  }

  private restoreState(key: string): void {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(this.storageKey(key));
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw) as Partial<SoListTablePersistedState>;
      if (parsed?.view === 'compact' || parsed?.view === 'dense') this.view.set(parsed.view);
      if (Array.isArray(parsed?.hiddenColumns)) this.hiddenColumns.set(parsed.hiddenColumns);
    } catch {
      return;
    }
  }

  private persistState(key: string, state: SoListTablePersistedState): void {
    try {
      localStorage.setItem(this.storageKey(key), JSON.stringify(state));
    } catch {
      return;
    }
  }
}
